import { Host, h } from "@stencil/core";
/** A custom input component that wraps the html input element is a mobile friendly component that supports a label, some help text and other features.
 * @slot label-prefix - Can be used to inject content before the labe.
 * @slot label-suffix - Can be used to inject content after the label.
 */
export class DnnFieldset {
    constructor() {
        /** Can be set to specify if the fieldset can be resized by the user. */
        this.resizable = "none";
    }
    /** Sets the fieldset to the focused state. */
    async setFocused() {
        this.focused = true;
    }
    /** Unsets the fieldset focused state. */
    async setBlurred() {
        this.focused = false;
    }
    /** Sets the fieldset to a disabled state. */
    async disable() {
        this.disabled = true;
    }
    /** Sets the fieldset to an enabled state. */
    async enable() {
        this.disabled = false;
    }
    /** Places the label on the top of the container. */
    async pinLabel() {
        this.floatLabel = false;
    }
    /** Places the label in the vertical middle of the container. */
    async unpinLabel() {
        this.floatLabel = true;
    }
    /** Sets the validity of the field. */
    async setValidity(valid, message) {
        this.invalid = !valid;
        this.customValidityMessage = message;
    }
    getContainerClasses() {
        const classes = ["container"];
        if (this.focused) {
            classes.push("focused");
        }
        if (this.disabled) {
            classes.push("disabled");
        }
        if (this.invalid) {
            classes.push("invalid");
        }
        if (this.floatLabel && !this.focused) {
            classes.push("float-label");
        }
        return classes.join(" ");
    }
    render() {
        return (h(Host, { key: '6032d3942b29cd3c0f97565075394cd1e3c3d080' }, h("div", { key: '56286b0dc351a443a45497a214d42f4c0d58ce59', class: this.getContainerClasses() }, this.label &&
            h("label", { key: 'df031cc19d53abbe9ec96242ae61fc8026755244' }, h("slot", { key: '2ba73cfd1c79973d13e4eaf6dd7eeaedad2c52a4', name: "label-prefix" }), this.label, h("slot", { key: '0cb656b916ebb3cf4a78d85d9ba861cde7c0e9d5', name: "label-suffix" })), h("div", { key: '9ce902ebdc8b5f6ae1db46cf6a0bf8c31d37b26d', class: "resizer", style: { resize: this.resizable, overflow: this.resizable == "none" ? "visible" : "auto" } }, h("div", { key: 'c4b4b4a39a8c6c659812cf40a8145d33fcf89527', class: "inner-container" }, h("slot", { key: '34671b9e42b7332dca7b341f8e170de4f94fc594' })))), this.invalid && this.customValidityMessage &&
            h("div", { key: 'e9786492395098be5e04e3676d1dd494a863a2ec', class: "error-message" }, this.customValidityMessage), !this.invalid &&
            h("div", { key: 'ab7b14d6608e04d00a23e97be7b6fe7799004516', class: "help-text" }, this.helpText)));
    }
    static get is() { return "dnn-fieldset"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["dnn-fieldset.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dnn-fieldset.css"]
        };
    }
    static get properties() {
        return {
            "focused": {
                "type": "boolean",
                "attribute": "focused",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If true the fieldset will display as focused."
                },
                "getter": false,
                "setter": false,
                "reflect": true
            },
            "disabled": {
                "type": "boolean",
                "attribute": "disabled",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If true, the fieldset will display as disabled."
                },
                "getter": false,
                "setter": false,
                "reflect": true
            },
            "invalid": {
                "type": "boolean",
                "attribute": "invalid",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If true, the  fieldset will display as invalid."
                },
                "getter": false,
                "setter": false,
                "reflect": true
            },
            "label": {
                "type": "string",
                "attribute": "label",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Sets the text of the fieldset label (caption)."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "floatLabel": {
                "type": "boolean",
                "attribute": "float-label",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If true, the label will float in the container, set false to show it on top."
                },
                "getter": false,
                "setter": false,
                "reflect": true
            },
            "helpText": {
                "type": "string",
                "attribute": "help-text",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Can be used to show some help text about this field."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "resizable": {
                "type": "string",
                "attribute": "resizable",
                "mutable": false,
                "complexType": {
                    "original": "\"none\" | \"both\" | \"horizontal\" | \"vertical\" | \"block\" | \"inline\"",
                    "resolved": "\"block\" | \"both\" | \"horizontal\" | \"inline\" | \"none\" | \"vertical\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Can be set to specify if the fieldset can be resized by the user."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"none\""
            }
        };
    }
    static get states() {
        return {
            "customValidityMessage": {}
        };
    }
    static get methods() {
        return {
            "setFocused": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Sets the fieldset to the focused state.",
                    "tags": []
                }
            },
            "setBlurred": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Unsets the fieldset focused state.",
                    "tags": []
                }
            },
            "disable": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Sets the fieldset to a disabled state.",
                    "tags": []
                }
            },
            "enable": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Sets the fieldset to an enabled state.",
                    "tags": []
                }
            },
            "pinLabel": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Places the label on the top of the container.",
                    "tags": []
                }
            },
            "unpinLabel": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Places the label in the vertical middle of the container.",
                    "tags": []
                }
            },
            "setValidity": {
                "complexType": {
                    "signature": "(valid: boolean, message?: string) => Promise<void>",
                    "parameters": [{
                            "name": "valid",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "message",
                            "type": "string | undefined",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Sets the validity of the field.",
                    "tags": []
                }
            }
        };
    }
}
//# sourceMappingURL=dnn-fieldset.js.map
